/**
 * Beat timeline dock. Columns are beats (left -> right = time); rows are lanes
 * (Camera / Focus / Narration / Hold). Concurrent actions in a beat stack into
 * their lane. Mirrors the mockup's timeline. Editing is enabled only when the
 * matching handler is supplied: native HTML5 DnD moves action blocks between
 * beats and drags beat headers to reorder whole beats (a drop on a beat's
 * left/right half inserts before/after it).
 */
import { useState, type DragEvent } from 'react';
import {
  LANES, beatDriver, blockHover, blockLabel, checkHover, checkLabel, continuousCamCarry, isContinuousCam, laneOf, laneTaken,
  type Beat, type EditorCheck, type EditorStep, type LaneKey,
} from './model';
import { DockDrawer, type DrawerShell } from './PanelShell';
import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '../Icon';
import { isNoopMove } from './layout';
import { useUnits } from '@/hooks/useUnits';

export type ActionSel = { bi: number; ai: number };

type DragState = { type: 'action'; sel: ActionSel } | { type: 'beat'; bi: number };
type DropState = { bi: number; pos: 'into' | 'before' | 'after' };

type TimelineProps = {
  step: EditorStep | null;
  beats: Beat[];
  /** The step's comprehension checks; each renders in the Checks lane of the
   *  beat it fires at the end of. */
  checks?: EditorCheck[];
  /** Index into `checks` of the selected check (its editor is in the
   *  inspector), or null. Mutually exclusive with `selAction`. */
  selCheck?: number | null;
  onSelectCheck?: (index: number | null) => void;
  /** Add a check to this beat (the "+" inside the Checks lane). A check can
   *  only be created attached to a beat, and only to a beat that has actions:
   *  the module refuses a check whose beat has no script row. */
  onAddCheck?: (bi: number) => void;
  /** Index of the beat the orientation playback is currently on (this stop), or
   *  null when not playing / another stop is playing. Drives the "now playing"
   *  column highlight. */
  playingBeat?: number | null;
  selAction: ActionSel | null;
  onSelectAction: (sel: ActionSel | null) => void;
  onAddBeat?: () => void;
  onDeleteBeat?: (bi: number) => void;
  onAddAction?: (bi: number, lane: LaneKey) => void;
  onMoveAction?: (from: ActionSel, toBi: number) => void;
  /** Drag reorder: move the beat at `from` to gap `insert` (0..length). */
  onMoveBeat?: (from: number, insert: number) => void;
  /** Bottom-drawer chrome from useEditorLayout. */
  shell: DrawerShell;
  /** Twin display names (TWIN-DISPLAY-NAME chain) for block captions. */
  twinNames?: Map<string, string>;
};

export function Timeline({ step, beats, checks = [], selCheck, onSelectCheck, onAddCheck, playingBeat, selAction, onSelectAction, onAddBeat, onDeleteBeat, onAddAction, onMoveAction, onMoveBeat, shell, twinNames }: TimelineProps) {
  const { units } = useUnits();
  const [drag, setDrag] = useState<DragState | null>(null);
  const [drop, setDrop] = useState<DropState | null>(null);
  const clearDrag = () => { setDrag(null); setDrop(null); };
  // A continuous camera control (Orbit/Follow) carries across later beats until
  // a Stop. camCarry[bi] is the still-running command to ghost in this beat.
  const camCarry = continuousCamCarry(beats);

  const beatDropClass = (bi: number): string => {
    if (drop?.bi !== bi) return '';
    if (drop.pos === 'into') return ' drop';
    return drop.pos === 'before' ? ' drag-before' : ' drag-after';
  };

  return (
    <DockDrawer
      shell={shell}
      title={step ? `Timeline · ${step.name}` : 'Timeline'}
      sub={step ? `${beats.length} beat${beats.length === 1 ? '' : 's'}` : undefined}
      headExtra={<span className="oe-label-up">each beat auto-advances on its longest action · a beat with checks holds until answered</span>}
    >
      <div className="oe-tl-body">
        {!step ? (
          <div className="oe-tl-empty">Select a stop to {onAddBeat ? 'author' : 'view'} its timeline.</div>
        ) : (
          <div className="oe-tl-scroll">
            <div className="oe-tl-gutter">
              <div className="oe-tl-corner" />
              {LANES.map((l) => (
                <div key={l.key} className={'oe-tl-lane-label ' + l.key} title={l.desc}>
                  <span className="dot" />{l.label}
                </div>
              ))}
            </div>
            {beats.length === 0 && (
              <div className="oe-tl-empty">
                <div>
                  No script for this stop yet.
                  {onAddBeat ? (
                    <div style={{ marginTop: 10 }}>
                      <button className="oe-btn primary sm" onClick={onAddBeat}>+ Add first beat</button>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
            {beats.map((beat, bi) => (
              <div
                key={bi}
                className={'oe-tl-beat' + beatDropClass(bi) + (playingBeat === bi ? ' playing' : '')
                  + (checks.some((c) => c.beat === bi) ? ' holding' : '')}
                onDragOver={(e) => {
                  if (!drag) return;
                  if (drag.type === 'action') {
                    if (!onMoveAction) return;
                    // A single-per-beat lane that is already filled in the target
                    // beat takes no drop (the move handler refuses it too).
                    const moving = beats[drag.sel.bi]?.actions[drag.sel.ai];
                    if (moving && drag.sel.bi !== bi && laneTaken(beats[bi], laneOf(moving))) return;
                    e.preventDefault();
                    setDrop({ bi, pos: 'into' });
                  } else {
                    if (!onMoveBeat) return;
                    e.preventDefault();
                    const r = e.currentTarget.getBoundingClientRect();
                    setDrop({ bi, pos: e.clientX > r.left + r.width / 2 ? 'after' : 'before' });
                  }
                }}
                onDragLeave={() => setDrop((c) => (c?.bi === bi ? null : c))}
                onDrop={(e) => {
                  if (!drag) return;
                  e.preventDefault();
                  if (drag.type === 'action' && onMoveAction) {
                    onMoveAction(drag.sel, bi);
                  } else if (drag.type === 'beat' && onMoveBeat && drop) {
                    const insert = drop.bi + (drop.pos === 'after' ? 1 : 0);
                    if (!isNoopMove(drag.bi, insert)) onMoveBeat(drag.bi, insert);
                  }
                  clearDrag();
                }}
              >
                <BeatHead
                  beat={beat}
                  bi={bi}
                  checkCount={checks.filter((c) => c.beat === bi).length}
                  playing={playingBeat === bi}
                  onDeleteBeat={onDeleteBeat}
                  draggable={!!onMoveBeat}
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', '');
                    e.dataTransfer.effectAllowed = 'move';
                    setDrag({ type: 'beat', bi });
                  }}
                  onDragEnd={clearDrag}
                />
                {LANES.map((l) => l.key === 'checks' ? (
                  // Checks are not actions: they hang off the step, keyed to
                  // the beat they fire at the END of, so this lane renders
                  // from `checks` and has its own add affordance.
                  <div key={l.key} className="oe-tl-cell lane-checks">
                    {checks.map((c, ci) => c.beat === bi ? (
                      <div
                        key={c.checkId}
                        className={'oe-tl-block kind-check' + (selCheck === ci ? ' sel' : '')}
                        title={checkHover(c)}
                        onClick={() => onSelectCheck?.(ci)}
                      >
                        <span className="bdot" />
                        <span className="bl">{checkLabel(c)}</span>
                      </div>
                    ) : null)}
                    {onAddCheck && (beat.actions.length ? (
                      <button className="oe-tl-add check" title="Add a comprehension check to this beat"
                        onClick={(e) => { e.stopPropagation(); onAddCheck(bi); }}>+</button>
                    ) : (
                      // A span, not a disabled button: the module refuses a
                      // check on a beat with no script row, and a disabled
                      // button swallows the hover that explains why.
                      <span className="oe-tl-add check off"
                        title="This beat presents nothing yet. Give it an action first: a check hangs off a beat of the script.">+</span>
                    ))}
                  </div>
                ) : (
                  <div key={l.key} className={'oe-tl-cell lane-' + l.key}>
                    {beat.actions.map((a, ai) => laneOf(a) === l.key ? (
                      <Block
                        key={ai}
                        label={blockLabel(a, twinNames)}
                        continuous={isContinuousCam(a)}
                        hover={blockHover(a, twinNames, units)}
                        kind={a.kind}
                        lane={l.key}
                        selected={selAction?.bi === bi && selAction?.ai === ai}
                        draggable={!!onMoveAction}
                        onClick={() => onSelectAction({ bi, ai })}
                        onDragStart={() => setDrag({ type: 'action', sel: { bi, ai } })}
                        onDragEnd={clearDrag}
                      />
                    ) : null)}
                    {l.key === 'camera' && camCarry[bi] && (
                      <div
                        className="oe-tl-cont"
                        title={[camCarry[bi]!.twinIds.join(', ') || undefined, 'Camera continues until a Stop camera'].filter(Boolean).join(' · ')}
                      >
                        <span className="bdot" /><Icon of={Icons.action.play} size={12} /> {blockLabel(camCarry[bi]!, twinNames)} continues…
                      </div>
                    )}
                    {onAddAction && (laneTaken(beat, l.key) ? (
                      <span className="oe-tl-add off" title={'One ' + l.label.toLowerCase() + ' action per beat: the trainee only gets one. Add another beat for the next.'}>+</span>
                    ) : (
                      <button className="oe-tl-add" title={'Add ' + l.label}
                        onClick={(e) => { e.stopPropagation(); onAddAction(bi, l.key); }}>+</button>
                    ))}
                  </div>
                ))}
              </div>
            ))}
            {onAddBeat && beats.length > 0 && (
              <button
                className="oe-tl-addbeat"
                onClick={onAddBeat}
                title="Add a beat: one column of actions that start together; the beat advances when its longest action finishes"
              >+ Beat</button>
            )}
          </div>
        )}
      </div>
    </DockDrawer>
  );
}

function BeatHead({ beat, bi, checkCount, playing, onDeleteBeat, draggable, onDragStart, onDragEnd }: {
  beat: Beat; bi: number; checkCount: number; playing: boolean; onDeleteBeat?: (bi: number) => void;
  draggable: boolean; onDragStart: (e: DragEvent) => void; onDragEnd: () => void;
}) {
  const drv = beatDriver(beat);
  return (
    <div
      className={'oe-tl-beat-head' + (draggable ? ' oe-grab' : '')}
      draggable={draggable}
      title={draggable ? 'Drag to reorder this beat' : undefined}
      onDragStart={draggable ? onDragStart : undefined}
      onDragEnd={draggable ? onDragEnd : undefined}
    >
      {draggable && <span className="oe-grip" aria-hidden><Icon of={Icons.action.dragHandle} size={12} /></span>}
      <span className="bn">{playing && <Icon of={Icons.action.play} size={10} />}BEAT {bi + 1}</span>
      {checkCount > 0 && (
        <span
          className="oe-tl-holds"
          title={`This beat presents, then holds until the trainee resolves its ${checkCount === 1 ? 'check' : `${checkCount} checks`}. Playback cannot run past it.`}
        >holds for check{checkCount > 1 ? 's' : ''}</span>
      )}
      <span
        className={'oe-tl-dur ' + (drv.ms ? drv.driver : '')}
        title={drv.ms
          ? `Estimated beat length. The beat advances when its longest action finishes; here that is the ${drv.driver === 'narration' ? 'narration' : drv.driver === 'camera' ? 'camera action' : 'hold'} (color-coded).`
          : 'No timed actions: this beat fires everything and advances immediately.'}
      >
        {drv.ms ? `≈${(drv.ms / 1000).toFixed(1)}s` : 'instant'}
      </span>
      <span className="oe-spacer" />
      {onDeleteBeat && (
        <button className="oe-tl-x" title="Delete beat" aria-label="Delete beat" onClick={() => onDeleteBeat(bi)}><Icon of={Icons.action.delete} size={14} /></button>
      )}
    </div>
  );
}

function Block({ label, continuous, kind, lane, selected, draggable, onClick, onDragStart, onDragEnd, hover }: {
  label: string; continuous?: boolean; kind: string; lane: LaneKey; selected: boolean; draggable: boolean;
  onClick: () => void; onDragStart: () => void; onDragEnd: () => void;
  /** Multi-line hover text (blockHover): what the action does + its params. */
  hover?: string;
}) {
  return (
    <div
      className={`oe-tl-block kind-${kind} lane-${lane}${selected ? ' sel' : ''}`}
      draggable={draggable}
      title={[hover, draggable ? 'Drag to move to another beat' : undefined].filter(Boolean).join('\n') || undefined}
      onClick={onClick}
      onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text', 'x'); onDragStart(); }}
      onDragEnd={onDragEnd}
    >
      <span className="bdot" />
      <span className="bl">{label}{continuous && <> <Icon of={Icons.action.play} size={10} />∞</>}</span>
    </div>
  );
}
